import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Stepper,
    Step,
    StepLabel,
    Theme,
    StepIcon,
    StepIconProps,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Collapse,
    Divider
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import {createQuiz} from '../../../../../Utils/apiService';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GradeIcon from '@mui/icons-material/Grade';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import GridViewIcon from '@mui/icons-material/GridView';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StarIcon from '@mui/icons-material/Star';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import LockIcon from '@mui/icons-material/Lock';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate, useLocation } from 'react-router-dom';
import TimeSettings from './Components/Timesettings';
import GradeSettings from './Components/GradeSettings';
import QuestionBehaviour from './Components/QuestionBehaviour';
import LayoutSettings from './Components/LayoutSettings';
import KenInput from '../../../../../GlobalComponents/KenInput';
import ReviewOptions from './Components/ReviewOptions';
import Appearance from './Components/Appearence';
import RestrictAccess from './Components/RestrictAccess';
import { QuizConfig, quizConfig, convertToSeconds } from "./Helper/AssessmentHelper"
import routes from '../../../../../Utils/routes';
import KenSnackbar from '../../../../../GlobalComponents/KenSnackbar';
import KenLoader from '../../../../../GlobalComponents/KenLoader';
// Define the form input types
interface IFormInputs {
    name: string;
    description: string;
}

export interface ITimeSetting {
    hours: string;
    minutes: string;
}

// Tab definition interface
interface Tab {
    id: string;
    label: string;
    icon: React.ReactNode;
}

// Create styles using makeStyles
const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        height: 'calc(100vh - 160px)',
        gap: '12px',
    },
    leftPanel: {
        width: 280,
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: theme.spacing(3),
        paddingBottom: 0,
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.05)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
    },
    rightPanel: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.05)',
    },
    contentArea: {
        flex: 1,
        padding: theme.spacing(2),
        overflowY: 'auto',
    },
    buttonArea: {
        padding: theme.spacing(2, 4),
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'flex-end',
        backgroundColor: '#fff',
        borderBottomLeftRadius: '12px',
        borderBottomRightRadius: '12px',
    },
    formControl: {
        marginBottom: theme.spacing(3),
    },
    inputLabel: {
        textAlign: 'left',
        display: 'block',
        marginBottom: theme.spacing(1),
        fontWeight: 500,
    },
    textField: {
        width: '100%',
    },
    select: {
        width: '100%',
    },
    textarea: {
        width: '100%',
        minHeight: 150,
    },
    cancelButton: {
        marginRight: theme.spacing(2),
    },
    saveButton: {
        backgroundColor: '#3986c8',
        '&:hover': {
            backgroundColor: '#2d6da3',
        },
    },
    stepperRoot: {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4),
    },
    step: {
        '& .MuiStepLabel-root': {
            alignItems: 'flex-start',
        },
        '& .MuiStepIcon-root': {
            color: '#3986c8',
        },
        '& .MuiStepIcon-root.Mui-active': {
            color: '#3986c8',
        },
        '& .MuiStepLabel-label': {
            marginTop: 0,
            textAlign: 'left',
        },
    },
    activeStep: {
        '& .MuiStepIcon-root': {
            color: '#3986c8',
        },
    },
    completedStep: {
        '& .MuiStepIcon-root': {
            color: '#4caf50',
        },
        '& .MuiStepLabel-label': {
            color: '#4caf50',
        },
    },
    title: {
        marginBottom: theme.spacing(4),
    },
    requiredIndicator: {
        color: 'red',
    },
    tabsList: {
        padding: 0,
        '& > .MuiListItem-root:last-child': {
            marginBottom: theme.spacing(1),
        },
    },
    advancedTabsList: {
        padding: 0,
        flexGrow: 1,
        overflowY: 'auto',
    },
    tabItem: {
        paddingLeft: 0,
        paddingRight: 0,
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            borderRadius: '8px',
        },
    },
    tabItemActive: {
        backgroundColor: 'rgba(57, 134, 200, 0.1)',
        borderRadius: '8px',
    },
    tabIcon: {
        minWidth: '40px',
        color: '#666',
    },
    tabText: {
        '& .MuiTypography-root': {
            fontWeight: 400,
            fontSize: '1rem',
        },
    },
    activeTabText: {
        '& .MuiTypography-root': {
            fontWeight: 500,
            color: '#3986c8',
        },
    },
    activeTabIcon: {
        color: '#3986c8',
    },
    advancedSettingButton: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#f1f7fc',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        padding: theme.spacing(2),
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#e4f1fa',
        },
    },
    advancedSettingText: {
        color: '#555',
        fontWeight: 500,
        fontSize: '1rem',
    },
    advancedIcon: {
        color: '#555',
        transition: 'transform 0.3s',
    },
    advancedIconExpanded: {
        transform: 'rotate(180deg)',
    },
    sectionTitle: {
        fontSize: '16px',
        fontWeight: '500 !important',
        color: '#333',
        marginBottom: theme.spacing(3),
        marginTop: theme.spacing(6),
        display: 'flex',
        alignItems: 'center',
        '&:first-child': {
            marginTop: 0,
        },
    },
    sectionIcon: {
        marginRight: theme.spacing(1),
        color: '#3986c8',
    },
    sectionContent: {
        marginBottom: theme.spacing(4),
        background: "#F6F8Fb",
        padding: theme.spacing(3),
        borderRadius: "12px"
    },
    sectionDivider: {
        margin: theme.spacing(4, 0),
    },
    assessmentSettings: {
        background: '#F6F8FB'
    },
    font16_500: {
        fontSize: "16px",
        fontWeight: "500"
    },
    font14_400: {
        fontSize: "14px",
        fontWeight: "400"
    }
}));

const CustomStepIcon = (props: StepIconProps) => {
    const { active, completed } = props;

    return (
        <>
            {completed ? (
                <CheckCircleIcon style={{ color: '#4caf50' }} />
            ) : (
                <StepIcon {...props} />
            )}
        </>
    );
};

const steps = ['General', 'Assessment settings'];


const mainTabs: Tab[] = [
    { id: 'time', label: 'Time', icon: <AccessTimeIcon /> },
    { id: 'grade', label: 'Grade', icon: <GradeIcon /> },
    { id: 'question', label: 'Question behaviour', icon: <QuestionAnswerIcon /> },
];

const advancedTabs: Tab[] = [
    { id: 'layout', label: 'Layout', icon: <GridViewIcon /> },
    { id: 'tags', label: 'Tags', icon: <LocalOfferIcon /> },
    { id: 'review', label: 'Review', icon: <StarIcon /> },
    { id: 'appearance', label: 'Appearance', icon: <ViewStreamIcon /> },
    { id: 'restrict', label: 'Restrict access', icon: <LockIcon /> },
];

const AssessmentForm: React.FC = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState<number[]>([]);
    const [activeTab, setActiveTab] = useState('time');
    const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
      const [isLoading, setIsLoading] = useState(false);
    const [sectionsInitialized, setSectionsInitialized] = useState(false);
      const [snackbarOpen, setSnackbarOpen] = useState(false);
        const [snackbarMessage, setSnackbarMessage] = useState('');
        const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const [assessmentData, setAssessmentData] = useState<QuizConfig>(quizConfig());
    const location = useLocation();
        const { title, courses, type, courseId, tab, id } = location.state || {
            title: '',
            courses: null,
            type: '',
            courseId: '',
            tab: ""
        };
        console.log(id, type, "courseId")
    const [timeSettings, setTimeSettings] = useState<ITimeSetting>({
        hours: "0",
        minutes: "00"
    });

    const contentRef = useRef<HTMLDivElement>(null);
    const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    const { control, handleSubmit } = useForm<IFormInputs>();

    const onSubmit: SubmitHandler<IFormInputs> = (data) => {
        const { name, description } = data;
        if (!completedSteps.includes(activeStep)) {
            setCompletedSteps([...completedSteps, activeStep]);
        }
        setActiveStep(1);
        setActiveTab('time');
        setAssessmentData({
            ...assessmentData,
            name,
            description
        })
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        let payload:any ={
            [name]:value
        }
        switch(name){
            case "overduehandling":{
                if(value!="graceperiod"){
                    payload={
                        ...payload,
                        graceperiod:0
                    }
                }
            };break;
            case "preferredbehaviour":{
                if(!["immediatefeedback","immediatecbm","interactive"].includes(value)){
                    payload={
                        ...payload,
                        canredoquestions:0
                    }
                }
            }
        }
        setAssessmentData({
            ...assessmentData,
            ...payload
        })
        console.log("assessmentData",name, value)
    }

    const handleCheckBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target
        setAssessmentData({
            ...assessmentData,
            [name]: checked
        })
    }

const handleSave = async () => {
    try {
        let payload: any = {
            method: "post",
            quizsettings: [
              {
                ...assessmentData,
                opentime: new Date(assessmentData.opentime).getTime(),
                closetime: new Date(assessmentData.closetime).getTime(),
                // timelimit: convertToSeconds(Number(assessmentData.timeLimitHours), Number(assessmentData.timeLimitMinutes)),
              }
            ]
          };
          
          // Properly format courseoffering as an array of objects with id
          payload.quizsettings[0].courseoffering = [
            {
              id: courses?.CourseOfferingID || ""
            }
          ];
          
          

        // API Call
        const response = await createQuiz(payload);

        if (response && response.status === 200) {
            console.log("Quiz created successfully:", response.data);
        } else {
            console.error("Failed to create quiz:", response);
        }
    } catch (error) {
        console.error("Error while creating quiz:", error);
    }
};

    const handleTabClick = (tabId: string) => {
        setActiveTab(tabId);
        // Scroll to the corresponding section
        if (sectionRefs.current[tabId] && contentRef.current) {
            sectionRefs.current[tabId]?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const toggleAdvancedSettings = () => {
        setAdvancedSettingsOpen(!advancedSettingsOpen);
    };

    // Initialize sections
    useEffect(() => {
        if (activeStep === 1) {
            // Use a short timeout to ensure elements are in the DOM
            const timer = setTimeout(() => {
                setSectionsInitialized(true);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [activeStep]);

    // Update active tab based on scroll position
    useEffect(() => {
        if (activeStep !== 1 || !contentRef.current || !sectionsInitialized) return;

        const handleScroll = () => {
            if (!contentRef.current) return;

            const scrollContainer = contentRef.current;
            const scrollPosition = scrollContainer.scrollTop;
            const containerHeight = scrollContainer.clientHeight;
            const containerScrollHeight = scrollContainer.scrollHeight;

            // Calculate the distance from the bottom of the scroll container
            const distanceFromBottom = containerScrollHeight - (scrollPosition + containerHeight);

            // If we're near the bottom, activate the last tab
            if (distanceFromBottom < 50) {
                const lastTab = advancedTabs[advancedTabs.length - 1].id;
                if (activeTab !== lastTab) {
                    setActiveTab(lastTab);
                    if (!advancedSettingsOpen) {
                        setAdvancedSettingsOpen(true);
                    }
                }
                return;
            }

            // Find which section is currently in view
            const allTabs = [...mainTabs, ...advancedTabs];
            let currentTab = allTabs[0].id;

            for (const tab of allTabs) {
                const sectionElement = sectionRefs.current[tab.id];
                if (sectionElement) {
                    // Get element position relative to the viewport
                    const rect = sectionElement.getBoundingClientRect();
                    const sectionTop = rect.top;

                    // Add a small offset to improve detection
                    if (sectionTop <= 150) {
                        currentTab = tab.id;
                    }
                }
            }

            if (currentTab !== activeTab) {
                setActiveTab(currentTab);

                // If the active tab is in the advanced settings, ensure it's open
                if (advancedTabs.some(tab => tab.id === currentTab) && !advancedSettingsOpen) {
                    setAdvancedSettingsOpen(true);
                }
            }
        };

        const contentElement = contentRef.current;
        contentElement.addEventListener('scroll', handleScroll);

        // Trigger initial scroll detection
        handleScroll();

        return () => {
            contentElement.removeEventListener('scroll', handleScroll);
        };
    }, [activeStep, activeTab, advancedSettingsOpen, sectionsInitialized]);

    const handleCancelClick = () => {
        navigate(`${routes.academics}`);
    };
    const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        console.log("Showing snackbar:", message, severity); // Debug log
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };
    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    return (
        <>
        <KenSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }} // Change to top position
    />
    {isLoading &&
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <KenLoader />
        </Box>
    }
        <Box className={classes.root}>
            <Paper className={classes.leftPanel} elevation={0}>
                <Stepper activeStep={activeStep} orientation="vertical" className={classes.stepperRoot}>
                    {steps.map((label, index) => (
                        <Step
                            key={label}
                            className={`
                                ${classes.step}
                                ${index === activeStep ? classes.activeStep : ''}
                                ${completedSteps.includes(index) ? classes.completedStep : ''}
                            `}
                            completed={completedSteps.includes(index)}
                        >
                            <StepLabel StepIconComponent={CustomStepIcon}>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {activeStep === 1 && (
                    <>
                        {/* Main tabs */}
                        <List className={classes.tabsList}>
                            {mainTabs.map((tab) => (
                                <ListItem
                                    key={tab.id}
                                    button
                                    className={`${classes.tabItem} ${activeTab === tab.id ? classes.tabItemActive : ''}`}
                                    onClick={() => handleTabClick(tab.id)}
                                >
                                    <ListItemIcon className={`${classes.tabIcon} ${activeTab === tab.id ? classes.activeTabIcon : ''}`}>
                                        {tab.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={tab.label}
                                        className={`${classes.tabText} ${activeTab === tab.id ? classes.activeTabText : ''}`}
                                    />
                                </ListItem>
                            ))}
                        </List>

                        <button
                            className={classes.advancedSettingButton}
                            onClick={toggleAdvancedSettings}
                        >
                            <span className={classes.advancedSettingText}>Show advanced setting</span>
                            <ExpandMoreIcon
                                className={`${classes.advancedIcon} ${advancedSettingsOpen ? classes.advancedIconExpanded : ''}`}
                            />
                        </button>

                        <Collapse in={advancedSettingsOpen}>
                            <List className={classes.advancedTabsList}>
                                {advancedTabs.map((tab) => (
                                    <ListItem
                                        key={tab.id}
                                        button
                                        className={`${classes.tabItem} ${activeTab === tab.id ? classes.tabItemActive : ''}`}
                                        onClick={() => handleTabClick(tab.id)}
                                    >
                                        <ListItemIcon className={`${classes.tabIcon} ${activeTab === tab.id ? classes.activeTabIcon : ''}`}>
                                            {tab.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={tab.label}
                                            className={`${classes.tabText} ${activeTab === tab.id ? classes.activeTabText : ''}`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Collapse>
                    </>
                )}
            </Paper>

            <Paper className={classes.rightPanel} elevation={0}>
                <Box className={classes.contentArea} ref={contentRef}>
                    {activeStep === 0 ? (
                        <form id="assessment-form" onSubmit={handleSubmit(onSubmit)}>
                            <Box className={classes.formControl}>
                                <Typography variant="body1" className={classes.inputLabel}>
                                    Assessment title <span className={classes.requiredIndicator}>*</span>
                                </Typography>
                                <Controller
                                    name="name"
                                    control={control}
                                    defaultValue=""
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            placeholder="Enter Assesment"
                                            variant="outlined"
                                            className={classes.textField}
                                        />
                                    )}
                                />
                            </Box>

                            {/* <Box className={classes.formControl}>
                                <Typography variant="body1" className={classes.inputLabel}>
                                    Select Program
                                </Typography>
                                <Controller
                                    name="program"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <FormControl variant="outlined" className={classes.select}>
                                            <Select
                                                {...field}
                                                displayEmpty
                                                renderValue={(selected) => {
                                                    if (selected === '') {
                                                        return <span style={{ color: '#aaa', fontStyle: 'normal', textAlign: 'left', fontFamily: 'inherit' }}>Select</span>;
                                                    }
                                                    return selected;
                                                }}
                                                sx={{
                                                    fontFamily: 'inherit',
                                                    textAlign: 'left',
                                                }}
                                            >
                                                <MenuItem value="" disabled sx={{ fontStyle: 'normal', fontFamily: 'inherit' }}>
                                                    Select
                                                </MenuItem>
                                                <MenuItem value="program1">Program 1</MenuItem>
                                                <MenuItem value="program2">Program 2</MenuItem>
                                                <MenuItem value="program3">Program 3</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                />
                            </Box> */}

                            <Box className={classes.formControl}>
                                <Typography variant="body1" className={classes.inputLabel}>
                                    Description
                                </Typography>
                                <Controller
                                    name="description"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            placeholder="Enter text"
                                            variant="outlined"
                                            multiline
                                            rows={6}
                                            className={classes.textarea}
                                        />
                                    )}
                                />
                            </Box>
                        </form>
                    ) : (
                        <div>
                            {/* Time Settings Section */}
                            <div
                                ref={(el) => sectionRefs.current['time'] = el}
                                id="time-section"
                            >
                                <Typography className={classes.sectionTitle}>
                                    Time Settings
                                </Typography>
                                <Box className={classes.sectionContent}>
                                    <TimeSettings
                                        assessmentData={assessmentData}
                                        timeSettings={timeSettings}
                                        setTimeSettings={setTimeSettings}
                                        setAssessmentData={setAssessmentData}
                                        handleChange={handleChange}
                                    />
                                </Box>
                                <Divider className={classes.sectionDivider} />
                            </div>

                            {/* Grade Settings Section */}
                            <div
                                ref={(el) => sectionRefs.current['grade'] = el}
                                id="grade-section"
                            >
                                <Typography className={classes.sectionTitle}>
                                    Grade Settings
                                </Typography>
                                <Box className={classes.sectionContent}>
                                    <FormControl fullWidth className={classes.formControl}>
                                        <GradeSettings data={assessmentData} handleChange={handleChange} from="assesment" />
                                    </FormControl>
                                </Box>
                                <Divider className={classes.sectionDivider} />
                            </div>

                            {/* Question behaviour Section */}
                            <div
                                ref={(el) => sectionRefs.current['question'] = el}
                                id="question-section"
                            >
                                <Typography className={classes.sectionTitle}>
                                    Question Behaviour
                                </Typography>
                                <Box className={classes.sectionContent}>
                                    <FormControl fullWidth className={classes.formControl}>
                                        <QuestionBehaviour assessmentData={assessmentData} handleChange={handleChange}/>
                                    </FormControl>
                                </Box>
                                <Divider className={classes.sectionDivider} />
                            </div>

                            {/* Layout Section */}
                            <div
                                ref={(el) => sectionRefs.current['layout'] = el}
                                id="layout-section"
                            >
                                <Typography className={classes.sectionTitle}>
                                    Layout Settings
                                </Typography>
                                <Box className={classes.sectionContent}>
                                    <FormControl fullWidth className={classes.formControl}>
                                        <LayoutSettings  assessmentData={assessmentData} handleChange={handleChange}/>
                                    </FormControl>
                                </Box>
                                <Divider className={classes.sectionDivider} />
                            </div>

                            {/* Tags Section */}
                            <div ref={(el) => (sectionRefs.current['tags'] = el)} id="tags-section">
                                <Typography className={classes.sectionTitle}>Tags</Typography>
                                <Box className={classes.sectionContent}>
                                    <FormControl fullWidth className={classes.formControl}>
                                        <Typography variant="body1" className={classes.inputLabel}>
                                            Tags(Press enter to add)
                                        </Typography>
                                        <KenInput
                                            placeholder="Type to add tags"
                                            variant="outlined"
                                            className={classes.textField}
                                        />
                                    </FormControl>
                                </Box>
                                <Divider className={classes.sectionDivider} />
                            </div>


                            {/* Review Section */}
                            <div
                                ref={(el) => sectionRefs.current['review'] = el}
                                id="review-section"
                            >
                                <Typography className={classes.sectionTitle}>
                                    Review Options
                                </Typography>
                                <Box className={classes.sectionContent}>
                                    <FormControl fullWidth className={classes.formControl}>
                                        <ReviewOptions />
                                    </FormControl>
                                </Box>
                                <Divider className={classes.sectionDivider} />
                            </div>

                            {/* Appearance Section */}
                            <div
                                ref={(el) => sectionRefs.current['appearance'] = el}
                                id="appearance-section"
                            >
                                <Typography className={classes.sectionTitle}>
                                    Appearance
                                </Typography>
                                <Box className={classes.sectionContent}>
                                    <FormControl fullWidth className={classes.formControl}>
                                        <Appearance assessmentData={assessmentData} handleChange={handleChange} />
                                    </FormControl>
                                </Box>
                                <Divider className={classes.sectionDivider} />
                            </div>

                            {/* Restrict Access Section */}
                            <div
                                ref={(el) => sectionRefs.current['restrict'] = el}
                                id="restrict-section"
                            >
                                <Typography className={classes.sectionTitle}>
                                    Restrict Access
                                </Typography>
                                <Box className={classes.sectionContent}>
                                    <FormControl fullWidth className={classes.formControl}>
                                        <RestrictAccess assessmentData={assessmentData} handleChange={handleChange}/>
                                    </FormControl>
                                </Box>
                            </div>
                        </div>
                    )}
                </Box>

                {/* Bottom fixed button area */}
                <Box className={classes.buttonArea}>
                    {activeStep === 1 && (
                        <Button
                            variant="text"
                            onClick={() => setActiveStep(0)}
                            style={{ marginRight: 'auto' }}
                        >
                            Back
                        </Button>
                    )}
                  <Button
  variant="text"
  className={classes.cancelButton}
 onClick={handleCancelClick}
>
  Cancel
</Button>
                    {activeStep === 0 ? (
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.saveButton}
                            type="submit"
                            form="assessment-form"
                        >
                            Save & proceed to assessment settings
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.saveButton}
                            onClick={handleSave}
                        >
                            Save settings
                        </Button>
                    )}
                </Box>
            </Paper>
        </Box>
        </>
    );
};

export default AssessmentForm;